import React from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { CheckCircle, Circle, Loader2, XCircle } from 'lucide-react';
import { cn } from '@/lib/utils';

interface SubmissionStep {
  id: string;
  label: string;
  status: 'pending' | 'loading' | 'success' | 'error';
}

interface SubmissionProgressProps {
  steps: SubmissionStep[];
  className?: string;
}

export const SubmissionProgress: React.FC<SubmissionProgressProps> = ({ 
  steps, 
  className 
}) => {
  if (steps.length === 0) return null;

  const getStepIcon = (status: SubmissionStep['status']) => {
    switch (status) {
      case 'loading':
        return <Loader2 className="h-4 w-4 animate-spin text-primary" />;
      case 'success':
        return <CheckCircle className="h-4 w-4 text-success" />;
      case 'error':
        return <XCircle className="h-4 w-4 text-destructive" />;
      case 'pending':
      default:
        return <Circle className="h-4 w-4 text-muted-foreground" />;
    }
  };

  const getStepTextColor = (status: SubmissionStep['status']) => {
    switch (status) {
      case 'loading':
        return 'text-primary';
      case 'success':
        return 'text-success';
      case 'error':
        return 'text-destructive';
      case 'pending':
      default:
        return 'text-muted-foreground';
    }
  };

  return (
    <Card className={cn("animate-fade-in", className)}>
      <CardContent className="p-4">
        <div className="space-y-3">
          {steps.map((step, index) => (
            <div key={step.id} className="flex items-center gap-3">
              <div className="flex-shrink-0">
                {getStepIcon(step.status)}
              </div>
              
              <span 
                className={cn(
                  "text-sm font-medium transition-colors",
                  getStepTextColor(step.status)
                )}
              >
                {step.label}
              </span>
              
              {step.status === 'loading' && (
                <div className="flex-1">
                  <div className="h-1 bg-muted rounded-full overflow-hidden">
                    <div className="h-full bg-primary rounded-full animate-pulse" />
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
};